import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { inr, usePageTitle, useShop } from "@/context/ShopContext";

export default function Cart() {
  usePageTitle("Shopping Bag");
  const { cartItems, setQty, removeFromCart, subtotal, settings } = useShop();
  const shipping = subtotal >= settings.free_shipping_threshold || subtotal === 0 ? 0 : settings.shipping_charge;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="cart-page">
      <header className="mb-12">
        <p className="overline-label mb-4">Your Selection</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">Shopping Bag</h1>
      </header>

      {cartItems.length === 0 ? (
        <div className="py-24 text-center" data-testid="cart-empty">
          <ShoppingBag className="w-8 h-8 text-taupe mx-auto" strokeWidth={1} />
          <p className="font-serif text-2xl mt-6">Your bag is empty</p>
          <p className="text-sm text-espresso/60 mt-3">Beautiful things are waiting.</p>
          <Link to="/shop" className="btn-primary mt-8" data-testid="cart-shop-link">Shop Hijabs</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 divide-y divide-beige border-t border-b border-beige">
            {cartItems.map((item) => {
              const { product, qty, key } = item;
              const slug = key.replace(/[^a-z0-9]+/gi, "-");
              return (
              <div key={key} className="py-6 flex gap-5" data-testid={`cart-item-${slug}`}>
                <Link to={`/product/${product.id}`} className="w-24 sm:w-28 shrink-0 bg-beige aspect-[4/5] overflow-hidden">
                  <img src={product.images[0]} alt={`${product.name} — ${product.shade}`} className="w-full h-full object-cover" loading="lazy" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Link to={`/product/${product.id}`} className="font-serif text-lg leading-snug hover:text-rose transition-colors">
                        {product.name}
                      </Link>
                      <p className="text-[11px] uppercase tracking-[0.2em] text-taupe mt-1">{product.shade}</p>
                      {item.selections && item.selections.length > 0 && (
                        <p className="text-[11px] text-taupe mt-1.5 leading-relaxed" data-testid={`cart-selections-${slug}`}>
                          {item.selections.join("  ·  ")}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`cart-remove-${slug}`}
                      aria-label={`Remove ${product.shade}`}
                      onClick={() => removeFromCart(key)}
                      className="p-1.5 text-taupe hover:text-espresso transition-colors"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <div className="inline-flex items-center border border-beige">
                      <button data-testid={`cart-qty-minus-${slug}`} aria-label="Decrease" onClick={() => setQty(key, qty - 1)} className="p-2.5 hover:bg-beige/50 transition-colors">
                        <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <span data-testid={`cart-qty-${slug}`} className="w-10 text-center text-sm">{qty}</span>
                      <button data-testid={`cart-qty-plus-${slug}`} aria-label="Increase" onClick={() => setQty(key, Math.min(20, qty + 1))} className="p-2.5 hover:bg-beige/50 transition-colors">
                        <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-sm">{inr(product.price * qty)}</p>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-beige p-8 lg:sticky lg:top-28" data-testid="order-summary">
              <h2 className="font-serif text-2xl">Order Summary</h2>
              <div className="space-y-4 mt-6 text-sm">
                <div className="flex justify-between">
                  <span className="text-espresso/70">Subtotal</span>
                  <span data-testid="summary-subtotal">{inr(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-espresso/70">Shipping</span>
                  <span data-testid="summary-shipping">{shipping === 0 ? "Complimentary" : inr(shipping)}</span>
                </div>
                {shipping > 0 && (
                  <p className="text-xs text-taupe">Complimentary shipping on orders above {inr(settings.free_shipping_threshold)}.</p>
                )}
                <div className="flex justify-between pt-4 border-t border-beige text-base font-medium">
                  <span>Total</span>
                  <span data-testid="summary-total">{inr(subtotal + shipping)}</span>
                </div>
              </div>
              <Link to="/checkout" data-testid="proceed-to-checkout" className="btn-primary w-full mt-8">
                Proceed to Checkout
              </Link>
              <Link to="/shop" className="block text-center text-xs uppercase tracking-[0.2em] text-taupe hover:text-espresso transition-colors mt-5">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
